import type {Feature} from "@k/js/product";
import {SelectedFeature, Product, getModelName} from "@k/js/product";
import type {AuswahlProdukt} from "@k/js/product";
import {HTMLPrinter} from "@k/global/htmlprinter";
import axios from "axios";
import {getGetParameterStringsFromKeyValueObject} from "@k/global/globalUtils";
import type {Produktresponse} from "@k/js/databaseInterfaceClasses"
import type {ProductList} from "@k/js/produktListe"
import {Store} from "vuex";
import {sendPostRequest} from "@k/global/axiosFormWrapper";
import {ProductController} from "@k/js/produktController";
import {sortProduktauswahlIntoOberkategorien} from "@k/global/produktAuswahl/sortProduktausahlntoOberkategorien";

export type ProduktAuswahlFeature = {
    Feature: string,
    Kuerzel: string,
    Name: string,
    Positione: number | null
    detailImageURL: string
    einzublendendeDreiDParts: string | null
    imKonfiBeiDerProduktuebersichtAuswaehlbar: string
    imKonfiVerstecktesAberFuerPreisberechnungRelevantesFeature: string
}

export type ProduktAuswahlFeaturedObject = {
    FeaturedObject: string,
    FeaturedObjectWeb: string,
    Features: ProduktAuswahlFeature[],
    Name: string,
    ShowImmerInRechnung: string,
    StandardFeature: string,
    positione: string,
    standardFeatureAbkuerzung: string,
    standardFeatureInt: string,
    zeigeZugehoerigeFeatureBeiFeatureAuswahlAn: string,
}

export type ProduktAuswahlProdukt = {
    Produkt: string,
    Name: string,
    Sportart: string,
    Realsport: string,
    websportUnterDemDasProduktGehostetIst: string,
    Urlname: string,
    Produktansicht: string | null,
    Designliste: string,
    Schnitt: string,
    Ordertype: string,
    Geschlecht: string,
    AuwiProdukt: string,
    DVSchnitt: string,
    FeaturedObjects: ProduktAuswahlFeaturedObject[],
    Regeln,
    preview: string,
    MengenBezeichner: string,
    discarded: boolean,
    derzeitNichtLieferbar,
    isArtikel: boolean,
    ArtikelWirdImIFrameDargestellt: boolean,
    ImKonfiKoennenPatternsVerwendetWerden: boolean
}

export type ProduktAuswahlDatenEntry = {
    category: string,
    hatEigeneURL: number,
    oberkategorie: string,
    products: ProduktAuswahlProdukt[],
    sex: string
}

export class ProduktAuswahlOberkategorie {
    public oberkategorieName;
    public categories = [] as ProduktAuswahlDatenEntry[];

    public constructor(oberkategorieName) {
        this.oberkategorieName = oberkategorieName;
    }
}



export class AuswahlProduktStoreWrapper implements ProductList {
    private store: Store<any>;
    private controller!: ProductController;

    public constructor(store: Store<any>) {
        this.store = store;
    }

    getAuswahlProdukte(): AuswahlProdukt[] {
        return this.store.getters.getprodukte
    }

    loadProdukte(): void {
        const getters = this.store.getters;
        this.store.dispatch("loadProdukte",
            {
                sportart: getters["ModelStore/getWebsportFuerProduktAuswahlNormalized"],
                sprache: getters["ModelStore/getSprache"],
                land: getters["ModelStore/getLand"]
            }
        )
        this.store.dispatch("loadProduktAuswahldaten",
            {
                sport: getters["ModelStore/getWebsportFuerProduktAuswahlNormalized"],
                sprache: getters["ModelStore/getSprache"],
                land: getters["ModelStore/getLand"]
            }
        )
    }

    setController(controller: ProductController): void {
        this.controller = controller;
    }
}

export default {
    state: () => ({
        produktListe: [] as AuswahlProdukt[],
        produktAuswahlDaten: [] as ProduktAuswahlDatenEntry[],
        auswahlProdukteSortedIntoOberkategorien: [] as ProduktAuswahlOberkategorie[],
        stagedProduct: null as null | Product,
        productPreviewPic: "",
        stagedAuswahlProdukt: null as null | AuswahlProdukt,
        artikelInfo: null as null | { artikel },
        productsToDisplayAtproductChange: [] as ProduktAuswahlProdukt[]
    }),
    getters: {
        getprodukte: state => state.produktListe,
        getUnterkategorie: (state, data: { oberkategorie: string, unterkategorie: string }) => {
            return state.produktAuswahlDaten.find(c => c.oberkategorie === data.oberkategorie && c.category === data.unterkategorie)
        },
        getProduktAuswahlDaten: state => state.produktAuswahlDaten,
        getProductsToDisplayAtproductChange: state => state.productsToDisplayAtproductChange,
        getAuswahlProdukteSortedIntoOberkategorien: store => store.auswahlProdukteSortedIntoOberkategorien,
        getStagedProduct: state => state.stagedProduct,
        getStagedAuswahlProdukt: state => state.stagedAuswahlProdukt,
        productHasFeaturedObjects: (state, productwebname) => {
            const prod = state.produktListe.find(p => p.name === productwebname);
            if (prod === undefined) {
                return false;
            }
            return prod.FeaturedObjects.length === 0;
        },
        getProductByProdukt: (state) => name => state.produktListe.find(p => p.Produkt === name),
        getProductByName: (state) => name => state.produktListe.find(p => p.Name === name),
        getChoosableFeatures: (state, getters) => productname => {
            const product = getters["getProductByProdukt"](productname);
            const featuredObject = product.FeaturedObjects;

            return featuredObject.filter(fo => {
                if (fo.Features.length < 2) {
                    return false;
                }
                if (fo.zeigeZugehoerigeFeatureBeiFeatureAuswahlAn === "0") {
                    return false;
                }
                return fo.Features.find(f => f.imKonfiBeiDerProduktuebersichtAuswaehlbar === "1") !== undefined;
            })
        },
        getStagedproductModel: (state) => state.stagedProduct?.model || "",
        getStagedProductSelectedFeatures: (state) => {
            return state.stagedProduct === null ? [] : state.stagedProduct.selectedFeatures
        },
        getStagedproductPossibleDesigns: state => state.stagedProduct?.possibleDesigns || [],
        getStagedproductRealSport: state => state.stagedProduct?.realsport || "",
        getProductpreviewPicture: state => state.productPreviewPic,
        getArtikelInfo: state => state.artikelInfo as { artikelBilder, artikelInfos, previewBilder }
    },
    mutations: {
        SET_PRODUKTLISTE(state, produkte) {
            state.produktListe = produkte
        },
        SET_PRODUCTS_TO_DISPLAY_AT_PRODUCTCHANGE(state, liste) {
            state.productsToDisplayAtproductChange = liste;
        },
        SET_PRODUKTAUSWAHL_DATEN(state, produktAuswahlDaten) {
            state.produktAuswahlDaten = produktAuswahlDaten;
            state.auswahlProdukteSortedIntoOberkategorien = sortProduktauswahlIntoOberkategorien(produktAuswahlDaten)
        },
        STAGE_PRODUCT(state, payload: { auswahlProdukt: AuswahlProdukt, features: Feature[], data: Produktresponse }) {
            state.stagedProduct = new Product(payload.data.Produkt, payload.data.Name, payload.data.Cut, payload.data.Ordertype, payload.data.auwiSport, payload.data.Designlist);
            state.stagedProduct.initAdditionalInfos(payload.data, payload.auswahlProdukt.Sportart, payload.features);
            state.stagedAuswahlProdukt = payload.auswahlProdukt;
        },
        STAGE_AUSWAHLPRODUKT(state, auswahlprodukt) {
            state.stagedAuswahlProdukt = auswahlprodukt
        },
        updateProductPreview(state, imgTag) {
            state.productPreviewPic = imgTag;
        },
        SET_STAGED_SELECTED_FEATURE(state, payload: { featuredObjectname: string, feature: Feature }) {
            const selectedfeature = new SelectedFeature(
                payload.featuredObjectname,
                payload.feature.Feature,
                payload.feature.Name,
                payload.feature.Kuerzel,
                payload.feature.einzublendendeDreiDParts,
                payload.feature.imKonfiBeiDerProduktuebersichtAuswaehlbar,
                payload.feature.imKonfiVerstecktesAberFuerPreisberechnungRelevantesFeature,
                payload.feature.isFeaturedObjectSichtbarLautDBAbfrage
            );
            (state.stagedProduct as Product).selectedFeatures = state.stagedProduct.selectedFeatures.filter(fo => fo.featuredObject !== payload.featuredObjectname);
            state.stagedProduct?.selectedFeatures.push(selectedfeature);
            state.stagedProduct.model = getModelName(state.stagedProduct.basemodel, state.stagedProduct.selectedFeatures);
        },
        SET_STAGED_PRODUCT_BASEMODEL(state, model: string) {
            if (state.stagedProduct !== null) {
                state.stagedProduct.basemodel = model;
            }
        },
        SET_ARTIKEL_INFO(state, artikelInfo) {
            state.artikelInfo = artikelInfo;
        }
    },
    actions: {
        loadProdukte(context, data: { sportart, sprache, land }) {
            const urlparam = HTMLPrinter.getURLParamString({
                "sportart": data.sportart,
                "language": data.sprache,
                "land": data.land
            });
            const url = "/konfigurator_php/auswahlmodul/produktAuswahl.php" + urlparam;
            axios({method: "get", url: url}).then((response) => context.commit("SET_PRODUKTLISTE", response.data))
        },
        loadProduktAuswahldaten(context, data: { sport, land, language, kundenKonfi }):void {
            const url = "/konfigurator_php/auswahlmodul/getProduktauswahlDaten.php" + getGetParameterStringsFromKeyValueObject(data);
            axios({
                method: "get",
                url: url
            }).then((response) => context.commit("SET_PRODUKTAUSWAHL_DATEN", response.data))
        },
        loadProductPreviewPic(context) {
            const stagedProduct = context.getters.getStagedProduct as Product;
            const path = escape(["", "modelle", stagedProduct.cut, stagedProduct.model, "Konfi-Voransichten", ""].join("/"));
            axios("/konfigurator_php/auswahlmodul/getproductPreviewPicture.php?path=" + path + "&design=" + stagedProduct.basisdesign + "&raw=1").then((result) => {
                context.commit("updateProductPreview", result.data);
            });
        },
        useStagedProductAsCurrentProduct(context) {
            context.commit("ModelStore/SET_CURRENT_OWAYO_PRODUCT", context.getters.getStagedProduct.clone());
            context.dispatch("ModelStore/loadInternationalLinks");
        },
        loadArtikelInfo(context, {product, sport}) {
            return new Promise(resolve => {
                sendPostRequest("/konfigurator_php/mobile/getArtikelInfos.php", {
                    language: context.getters["ModelStore/getSprache"],
                    land: context.getters["ModelStore/getLand"],
                    sport: sport,
                    product
                }).then(response => {
                    for (const p in response.data.artikelInfos) {
                        response.data.artikelInfos[p].artikelPreise = JSON.parse(response.data.artikelInfos[p].artikelPreise);
                    }
                    resolve(response.data)
                })
            })

        }
    }
}
